import type { SandboxProviderHealthStatus } from '@reflex/shared';

export type SandboxGateDecision = 'allow' | 'redirect-setup' | 'key-invalid-screen';

/**
 * A sandbox health result that can leave setup. `trial` is authenticated and
 * usable with a resource-size cap; `undefined` is intentionally excluded so a
 * loading or failed health request never redirects a user out of setup.
 */
export function isUsableSandboxHealth(
  status: SandboxProviderHealthStatus | undefined,
): status is 'ok' | 'trial' {
  return status === 'ok' || status === 'trial';
}

/**
 * Pure decision for the org-setup gate once the active org's sandbox key
 * health is known. Extracted from `OrgGate` so the branching is unit-testable
 * without rendering the whole `App` tree.
 *
 * - `allow` — render the app (or wizard) as normal. Used when the key is
 *   healthy, when its status is still unknown (loading / health-check error —
 *   fail open so a flaky check can't lock everyone out; the point-of-use
 *   `runloop_key_invalid` response is the safety net), when the key belongs
 *   to a `trial` Runloop account (see below), or when the user is already on
 *   the setup path (so the wizard can run).
 * - `redirect-setup` — bounce a user who can manage the org into the wizard's
 *   Runloop step to re-enter a working key.
 * - `key-invalid-screen` — show a contact-an-admin screen to users who can't
 *   fix the key themselves.
 *
 * `trial` is treated as `allow`, not as a re-enter-the-key state. A trial key
 * is authenticated and valid — it resolved a real Runloop account — it just
 * has its resource-size requests capped server-side. Trial/seeded orgs are
 * provisioned on a trial-tier key on purpose,
 * so hard-gating `trial` traps them in the setup wizard forever: the gate
 * bounces them to `runloop`, the wizard skips that already-satisfied step and
 * clamps forward to `done`, and finishing setup re-triggers the gate.
 * Only `missing` / `invalid` keys — which can't authenticate at all — warrant
 * sending the user back to re-enter a working key.
 */
export function resolveSandboxGateDecision(params: {
  status: SandboxProviderHealthStatus | undefined;
  canManage: boolean;
  onSetupPath: boolean;
}): SandboxGateDecision {
  const { status, canManage, onSetupPath } = params;
  if (status === undefined || isUsableSandboxHealth(status) || onSetupPath) {
    return 'allow';
  }
  return canManage ? 'redirect-setup' : 'key-invalid-screen';
}
